<template>
    <div class="h-full w-full flex flex-col">
        <div class="h-full container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col h-full w-full py-4 sm:py-8">
                <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
                    <div class="flex items-center text-sm font-light">
                        <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                        <span class="mx-2">/</span>
                        <span>Awarding Students</span>
                    </div>
                    <div class="font-bold">Awarding Students</div>
                </div>

                <div class="mt-4 flex justify-end items-center">
                    <div>
                        <div v-if="isExportExcel"
                            class="w-44 flex items-center justify-center hover:bg-white bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg hover:text-ternary text-white">
                            <base-spinner-with-text class-list="w-4 h-4" text=""
                                text-class-list="text-white"></base-spinner-with-text>
                            <span class="text-xs">Exporting Excel</span>
                        </div>
                        <div v-else @click="generateReport"
                            class="w-44 flex items-center justify-center hover:bg-white bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg cursor-pointer hover:text-ternary text-white">
                            <font-awesome-icon :icon="['fas', 'file-excel']" />
                            <span class="text-xs ml-2">Export Excel</span>
                        </div>
                    </div>
                </div>

                <section class="mt-4 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
                    <div class="flex justify-end flex-wrap">
                        <div class="flex justify-start items-center flex-wrap w-full">
                            <div
                                class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                <div class="text-sm mr-2">Entity:</div>
                                <select v-model="filterByEntity" class="outline-none text-sm flex-grow">
                                    <option value="">All</option>
                                    <option :value="item.entity" v-for="(item, index) in filteredEntities" :key="index">
                                        {{ getEntityName(item.entity) }}</option>
                                </select>
                            </div>

                            <div class="w-full sm:w-40 mr-0 sm:mr-8 mb-4 border rounded-lg px-4 py-2 h-auto flex justify-between items-center relative cursor-pointer"
                                :class="activeDropDown == 'campus' ? 'z-30' : ''">
                                <div class="flex flex-grow justify-between items-center"
                                    @click.stop="showCampusDropDown = !showCampusDropDown">
                                    <div class="text-sm mr-2 sm:mb-0 flex-shrink-0 flex-grow">
                                        Campus:
                                    </div>
                                    <icon-chevron-down class="h-4 w-4"></icon-chevron-down>
                                </div>
                                <div v-show="showCampusDropDown"
                                    class="flex flex-col max-h-80 overflow-y-auto shadow-lg rounded-lg px-2 pt-2 absolute w-full bg-white border left-0 top-10">
                                    <div v-for="(item, index) in filteredCampuses" :key="index"
                                        class="flex items-center mb-2 cursor-pointer">
                                        <input type="checkbox" :value="item.campus_code" :id="`campus-${index}`"
                                            v-model="filterByCampuses" class="mr-2" />
                                        <label :for="`campus-${index}`" class="text-sm">{{
                                            item.campus_code
                                        }}</label>
                                    </div>
                                </div>
                            </div>

                            <div
                                class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                <div class="text-sm mr-2">Local or Non Local:</div>
                                <select v-model="filterByLocalOrNonLocal" class="outline-none text-sm flex-grow">
                                    <option value="">All</option>
                                    <option value="Local">Local</option>
                                    <option value="Non Local">Non Local</option>
                                </select>
                            </div>

                            <div
                                class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                <div class="text-sm mr-2">Gender:</div>
                                <select v-model="filterByGender" class="outline-none text-sm flex-grow">
                                    <option value="">All</option>
                                    <option value="M">Male</option>
                                    <option value="F">Female</option>
                                </select>
                            </div>

                            <div
                                class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                <div class="text-sm mr-2">Registration Status:</div>
                                <select v-model="filterByRegistration" class="outline-none text-sm flex-grow">
                                    <option value="">All</option>
                                    <option value="Yes">Registered</option>
                                    <option value="No">Not Registered</option>
                                </select>
                            </div>

                            <div
                                class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                <div class="text-sm mr-2">{{ getSpecializationTitle }}:</div>
                                <select v-model="filterBySpecialization" class="outline-none text-sm flex-grow">
                                    <option value="">All</option>
                                    <option :value="item" v-for="(item, index) in specializationsList" :key="index">{{
                                        item }}</option>
                                </select>
                            </div>

                            <div class="w-full sm:w-auto mb-4">
                                <search @on-search="onSearch" class-list="px-4 h-8"></search>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-start">
                        <div @click.stop="clearAllFilter()"
                            class="px-3 py-1 text-sm border border-red-500 bg-red-500 hover:bg-white text-white hover:text-red-500 rounded-lg cursor-pointer">
                            Clear All Filter
                        </div>
                    </div>

                    <div class="flex flex-col" v-if="$apollo.queries.students.loading">
                        <div class="flex flex-col overflow-x-auto">
                            <div class="inline-block min-w-full py-2">
                                <div class="overflow-x-auto">
                                    <div class="min-w-full text-left text-sm font-light">
                                        <div class="border-b font-medium dark:border-neutral-500">
                                            <div class="flex">
                                                <div class="px-6 py-2 text-gray-400 w-12 lg:w-16">
                                                    #
                                                </div>
                                                <div class="flex-grow px-6 py-2 text-gray-400">
                                                    <div class="w-32 md:w-auto">Name</div>
                                                </div>
                                                <div class="px-6 py-2 text-gray-400">
                                                    <div class="w-24 lg:w-40">Entity</div>
                                                </div>
                                                <div class="px-6 py-2 text-gray-400">
                                                    <div class="w-24 lg:w-40">
                                                        {{ getSpecializationTitle }}
                                                    </div>
                                                </div>
                                                <div class="px-6 py-2 text-gray-400">
                                                    <div class="w-24 lg:w-40">Is Registered</div>
                                                </div>
                                                <div v-if="hasPermission('awarding-students-edit')"
                                                    class="px-6 py-2 text-gray-400 w-24 lg:w-40">
                                                    Awarded
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <student-tile-shimmer v-for="(sts, index) in first"
                                                :key="index"></student-tile-shimmer>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="noItems" class="h-full flex justify-center items-center">
                        <h1 class="text-center text-xl font-medium">No students found!</h1>
                    </div>
                    <div v-else class="flex flex-col">
                        <div class="flex flex-col overflow-x-auto">
                            <div class="inline-block min-w-full py-2">
                                <div class="overflow-x-auto">
                                    <table class="min-w-full text-left text-sm font-light">
                                        <thead class="border-b font-medium dark:border-neutral-500">
                                            <tr>
                                                <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                                                    #
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Name
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Entity
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Campus
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    {{ getSpecializationTitle }}
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Is Registered
                                                </th>
                                                <th v-if="hasPermission('awarding-students-edit')" scope="col"
                                                    class="px-6 py-2 text-gray-400">
                                                    Awarded
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(student, index) in filteredStudents" :key="index"
                                                class="border-b border-dashed dark:border-neutral-500">
                                                <td class="whitespace-nowrap px-6 py-2 font-medium">
                                                    {{ paginatorInfo.firstItem + index }}
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    <div class="flex items-center">
                                                        <div class="flex-shrink-0 mr-3">
                                                            <img @click.stop="
                                                                openPreviewImgModal(
                                                                    student.profile_picture_url,
                                                                    student.student_id,
                                                                    student.entity
                                                                )
                                                                "
                                                                class="h-12 w-12 rounded-lg object-cover cursor-pointer"
                                                                :src="getProfilePicture(
                                                                    student.profile_picture_url,
                                                                    student.student_id,
                                                                    student.entity
                                                                )
                                                                    " alt="" />
                                                        </div>
                                                        <div class="flex-grow flex flex-col">
                                                            <h1 class="text-sm font-semibold flex items-center">
                                                                <span @click.stop="
                                                                    viewStudentProfile(student.student_id)
                                                                    "
                                                                    class="text-heading hover:text-blue-600 hover:underline cursor-pointer">{{
                                                                        student.name_en }}</span>
                                                            </h1>
                                                            <p class="text-xs text-gray-400">
                                                                {{ student.name_ar }}
                                                            </p>
                                                            <p class="text-xs text-gray-400">
                                                                {{ student.student_id }}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    {{ getEntityName(student.entity) }}
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    {{ student.campus_code }}
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    {{
                                                        getSpecialization(
                                                            student.entity,
                                                            student.study_program,
                                                            student.program_name,
                                                            student.program_code,
                                                            student.cluster_e
                                                        )
                                                    }}
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    {{ isRegistered(student.user_id) }}
                                                </td>
                                                <td class="px-6 py-2">
                                                    <div class="flex">
                                                        <base-toggle v-if="hasPermission('awarding-students-edit')"
                                                            :toggle="isStudentAwarded(student.is_awarded)"
                                                            :id="student.student_id"
                                                            @set-checkbox-val="setCheckboxVal"></base-toggle>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div class="mt-4">
                            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                        </div>
                    </div>

                    <transition name="fade" mode="out-in">
                        <base-modal v-if="previewImageModal" title="" height="50vh" @close="closePreviewImageModal">
                            <img class="h-full w-full object-contain" :src="previewImg" />
                        </base-modal>
                    </transition>

                    <transition name="fade" mode="out-in">
                        <base-full-modal v-if="showStudentProfileFullModal" title="Student Profile"
                            @close="closeFullModal">
                            <student-profile :student-id="studentId"></student-profile>
                        </base-full-modal>
                    </transition>

                    <transition name="fade" mode="out-in">
                        <base-modal v-if="showUploadDocumentModal" :title="`Upload Documents`" height=""
                            @close="closeBaseModal">
                            <upload-documents @document-submit="onDocumentSubmit"
                                @close="closeBaseModal"></upload-documents>
                        </base-modal>
                    </transition>

                </section>
            </div>
        </div>
        <div class="h-full flex justify-center items-center" v-if="isExportExcel || isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
    </div>
</template>
<script>
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import StudentTileShimmer from "../../components/students/StudentTileShimmer.vue";
import Search from "../../components/shared/search.vue";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import StudentProfile from "../student-profile/index.vue";
import IconChevronDown from "../../components/icons/IconChevronDown.vue";
import UploadDocuments from "../pipeline-partners/upload-documents.vue";
export default {
    name: "awarding-students-index",
    components: {
        Pagination,
        StudentTileShimmer,
        Search,
        StudentProfile,
        IconChevronDown,
        UploadDocuments,
    },
    data() {
        return {
            filterByStatus: "AS",
            isFinalYearStudent: true,
            isAwarded: 'yes',
            first: 10,
            page: 1,
            students: [],
            entities: [],
            studentId: null,
            searchText: "",
            filterByEntity: "",
            filterByCampuses: [],
            filterByLocalOrNonLocal: "",
            filterByGender: "",
            filterByRegistration: "",
            filterBySpecialization: "",
            campusList: [],
            specializationsList: [],
            previewImageModal: false,
            previewImg: "",
            showFullModal: false,
            showStudentProfileFullModal: false,
            isExportExcel: false,
            isApiCallRecurisive: false,
            generatedReport: [],
            activeDropDown: "",
            showCampusDropDown: false,
            selectedStudentId: null,
            showUploadDocumentModal: false,
            isLoading: false,
        };
    },
    created() {
        this.getSpecializations();
        this.getCampusList();
    },
    watch: {
        searchText() {
            this.page = 1;
        },
        filterByEntity() {
            this.clearAllFilter("filterByEntity");
            this.page = 1;
            this.getSpecializations();
        },
        filterByCampuses() {
            this.page = 1;
        },
        filterByLocalOrNonLocal() {
            this.page = 1;
        },
        filterByGender() {
            this.page = 1;
        },
        filterByRegistration() {
            this.page = 1;
        },
        filterBySpecialization() {
            this.page = 1;
        },
        showCampusDropDown(newVal) {
            if (newVal) {
                this.activeDropDown = "campus";
                this.collapseDropDown("campus");
            }
        },
    },
    computed: {
        getSpecializationTitle() {
            if (this.filterByEntity == "ATHS" || this.filterByEntity == "ATS") {
                return "Cluster";
            } else {
                return "Program";
            }
        },
        noItems() {
            if (
                !this.$apollo.queries.students.loading &&
                this.filteredStudents.length <= 0
            ) {
                return true;
            } else {
                return false;
            }
        },
        isATS() {
            if (this.filterByEntity == "ATHS" || this.filterByEntity == "ATS") {
                return true;
            } else {
                return false;
            }
        },
        filteredCampuses() {
            if (this.filterByEntity) {
                return this.campusList.filter(
                    (campus) => campus.entity === this.filterByEntity
                );
            } else {
                return this.campusList;
            }
        },
        filteredStudents() {
            return this.students.data;
        },
        paginatorInfo() {
            return this.students.paginatorInfo;
        },
        role() {
            return this.$store.getters.role;
        },
        userEntities() {
            return this.$store.getters.entity;
        },
        filteredEntities() {
            return this.entities.data;
        },
    },
    apollo: {
        entities() {
            return {
                query: gql`
                    query GetEntityList {
                        entities {
                        data {
                            entity
                        }
                        }
                    }
                `,
                fetchPolicy: "cache-and-network",
            };
        },
        students() {
            return {
                query: gql`
                    query GetAllStudents(
                        $first: Int!
                        $page: Int!
                        $search: String
                        $entity: String
                        $student_status: String
                        $is_final_year_student: Boolean
                        $is_awarded: String
                        $campuses: [String]
                        $local_or_non_local: String
                        $gender: String
                        $filter_by_registration: String
                        $specialization: String
                    ) {
                        students(
                        first: $first
                        page: $page
                        search: $search
                        entity: $entity
                        student_status: $student_status
                        is_final_year_student: $is_final_year_student
                        is_awarded: $is_awarded
                        campuses: $campuses
                        local_or_non_local: $local_or_non_local
                        gender: $gender
                        filter_by_registration: $filter_by_registration
                        specialization: $specialization
                        ) {
                        data {
                            user_id
                            name_ar
                            name_en
                            student_id
                            entity
                            campus_code
                            eid
                            program_code
                            program_name
                            study_program
                            cluster_e
                            profile_picture_url
                            is_awarded
                        }
                        paginatorInfo {
                            count
                            currentPage
                            firstItem
                            hasMorePages
                            lastItem
                            lastPage
                            perPage
                            total
                        }
                        }
                    }
                `,
                variables() {
                    return {
                        first: this.first,
                        page: this.page,
                        search: this.searchText,
                        entity: this.filterByEntity,
                        specialization: this.filterBySpecialization,
                        student_status: this.filterByStatus,
                        is_final_year_student: this.isFinalYearStudent,
                        is_awarded: this.isAwarded,
                        campuses: this.filterByCampuses,
                        local_or_non_local: this.filterByLocalOrNonLocal,
                        gender: this.filterByGender,
                        filter_by_registration: this.filterByRegistration,
                    };
                },
                fetchPolicy: "cache-and-network",
            };
        },
    },
    methods: {
        collapseDropDown(dropDownName = "") {
            if (dropDownName) {
                if (dropDownName != "campus") {
                    this.showCampusDropDown = false;
                }
            } else {
                this.showCampusDropDown = false;
            }
        },
        clearAllFilter(filterName = "") {
            if (!filterName) {
                this.filterByEntity = "";
            }
            this.filterByCampuses = [];
            this.filterByLocalOrNonLocal = "";
            this.filterByGender = "";
            this.filterByRegistration = "";
            this.filterBySpecialization = "";
        },
        async getSpecializations() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetSpecializations(
                        $student_status: String
                        $entity: String
                        ) {
                        getSpecializations(
                            student_status: $student_status
                            entity: $entity
                        )
                        }
                    `,
                    variables: {
                        student_status: this.filterByStatus,
                        entity: this.filterByEntity,
                    },
                });
                this.specializationsList = data.getSpecializations;
            } catch (error) {
                console.log(error);
            }
        },
        async getCampusList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetCampusList($student_status: String, $entity: String) {
                        getCampusList(student_status: $student_status, entity: $entity) {
                            entity
                            campus_code
                        }
                        }
                    `,
                    variables: {
                        student_status: this.filterByStatus,
                        entity: this.filterByEntity,
                    },
                    fetchPolicy: "network-only",
                });
                this.campusList = data.getCampusList;
            } catch (error) {
                console.log(error);
            }
        },
        getEntityName(entity) {
            if (entity == "ATHS") {
                return "ATS";
            } else {
                return entity;
            }
        },
        getGender(gender) {
            if (gender) {
                if (gender == "M") {
                    return "Male";
                } else {
                    return "Female";
                }
            } else {
                return "";
            }
        },
        formatDate(dateString) {
            if (!dateString) return "";
            const [year, month, day] = dateString.split("-");
            return `${day}-${month}-${year}`;
        },
        getSpecialization(
            entity,
            studyProgram,
            programName,
            programCode,
            clusterE
        ) {
            let specialization = "";
            if (entity == "ATHS") {
                if (clusterE) {
                    specialization = clusterE;
                } else if (programName) {
                    specialization = programName;
                } else if (studyProgram) {
                    specialization = studyProgram;
                } else {
                    specialization = programCode;
                }
            } else {
                if (programName) {
                    specialization = programName;
                } else if (studyProgram) {
                    specialization = studyProgram;
                } else {
                    specialization = programCode;
                }
            }
            return specialization;
        },
        getEmployerName(isEmployed, employerInformation) {
            if (isEmployed) {
                if (employerInformation) {
                    return employerInformation.employer;
                } else {
                    return "Not Contacted";
                }
            } else {
                if (employerInformation) {
                    return employerInformation.employer;
                } else {
                    return "Unemployed";
                }
            }
        },
        getDesignation(employerInformation) {
            if (employerInformation) {
                if (employerInformation.position) {
                    return employerInformation.position;
                } else {
                    return employerInformation.aprehis_empl_position;
                }
            } else {
                return "";
            }
        },
        getAcademicYear(academicYear, AY) {
            if (academicYear) {
                return academicYear;
            } else if (AY) {
                return AY;
            } else {
                return "";
            }
        },
        getGraduationYear(gradYear, academicYearGraduation) {
            if (gradYear) {
                return gradYear;
            } else if (academicYearGraduation) {
                return academicYearGraduation;
            } else {
                return "";
            }
        },
        isRegistered(userId) {
            if (userId) {
                return "Yes";
            } else {
                return "No";
            }
        },
        setPage(page) {
            this.page = page;
        },
        getProfilePicture(profilePictureUrl, studentId, entity) {
            if (profilePictureUrl) {
                if (
                    profilePictureUrl ==
                    "/storage/alumni-profile-pictures/default-avatar.jpg"
                ) {
                    return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
                } else {
                    return process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
                }
            } else {
                return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
            }
        },
        onSearch(searchText) {
            this.searchText = searchText;
        },
        openPreviewImgModal(profilePictureUrl, studentId, entity) {
            if (profilePictureUrl) {
                if (
                    profilePictureUrl ==
                    "/storage/alumni-profile-pictures/default-avatar.jpg"
                ) {
                    this.previewImg = `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
                } else {
                    this.previewImg = process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
                }
            } else {
                this.previewImg = `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
            }
            this.previewImageModal = true;
        },
        closePreviewImageModal() {
            this.previewImageModal = false;
            this.previewImg = "";
        },
        hasPermission(crudName) {
            if (this.role == "superadmin") {
                return true;
            } else {
                const permissions = this.$store.getters.permissions;
                const permissionsArray = permissions.split(",");
                if (permissionsArray.includes(crudName)) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        async generateReport() {
            var page = 1;
            var limit = 200;
            this.isExportExcel = true;
            this.isApiCallRecurisive = true;

            try {
                while (this.isApiCallRecurisive) {
                    try {
                        const responseData = await this.fetchForExporting(page, limit);

                        if (responseData.data && responseData.data.length > 0) {
                            this.generatedReport.push(
                                ...responseData.data.map((obj) => {
                                    return {
                                        student_id: obj.student_id,
                                        eid: obj.eid,
                                        name_en: obj.name_en,
                                        name_ar: obj.name_ar,
                                        gender: this.getGender(obj.gender),
                                        birthdate: this.formatDate(obj.birthdate),
                                        entity: this.getEntityName(obj.entity),
                                        campus: obj.campus_code,
                                        program_name: this.getSpecialization(
                                            obj.entity,
                                            obj.study_program,
                                            obj.program_name,
                                            obj.program_code,
                                            obj.cluster_e
                                        ),
                                        ...(this.isATS ? { stream: obj.stream } : {}),
                                        alumni_phone: obj.alumni_phone || "",
                                        primary_phone: obj.primary_phone || "",
                                        secondary_phone: obj.secondary_phone || "",
                                        primary_email: obj.primary_email || "",
                                        secondary_email: obj.secondary_email || "",
                                        local_or_non_local: obj.local_or_non_local,
                                        academic_year: this.getAcademicYear(obj.academic_year, obj.ay),
                                        grad_year: this.getGraduationYear(
                                            obj.grad_year,
                                            obj.academic_year_graduation
                                        ),
                                        university_country: obj.university_country,
                                        university_name: obj.university_name,
                                        college: obj.college,
                                        major: obj.major,
                                        current_status: obj.current_status,
                                        current_status_reason: obj.current_status_reason,
                                        is_employed: obj.is_employed ? "Yes" : "No",
                                        employer: this.getEmployerName(
                                            obj.is_employed,
                                            obj.employmentInformations
                                        ),
                                        designation: this.getDesignation(obj.employmentInformations),
                                        registered: this.isRegistered(obj.user_id),
                                    };
                                })
                            );
                            page++;
                        } else {
                            this.isApiCallRecurisive = false;
                            break;
                        }
                    } catch (error) {
                        console.error(`Error fetching page ${page}:`, error);

                        // Show error to user but continue with partial data
                        this.$swal.fire({
                            icon: 'warning',
                            title: 'Export Warning',
                            text: `Error occurred while fetching page ${page}. Exporting partial data.`,
                            timer: 3000,
                            showConfirmButton: false
                        });

                        this.isApiCallRecurisive = false;
                        break;
                    }
                }

                if (this.generatedReport.length > 0) {
                    this.exportToExcel("Awarding_Students");
                } else {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Export Failed',
                        text: 'No data available for export.',
                    });
                }
            } catch (error) {
                console.error('Error in generateReport:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Export Failed',
                    text: 'An error occurred while generating the report.',
                });
            } finally {
                this.isExportExcel = false;
                this.isApiCallRecurisive = false;
            }
        },
        async fetchForExporting(pageNo = 1, limitSize = 50) {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GenerateStudentsReport(
                        $page: Int
                        $limit: Int
                        $search: String
                        $entity: String
                        $student_status: String
                        $is_final_year_student: Boolean
                        $is_awarded: String
                        $campuses: [String]
                        $local_or_non_local: String
                        $gender: String
                        $filter_by_registration: String
                        $specialization: String
                        ) {
                        generateStudentsReport(
                            page: $page
                            limit: $limit
                            search: $search
                            entity: $entity
                            student_status: $student_status
                            is_final_year_student: $is_final_year_student
                            is_awarded: $is_awarded
                            campuses: $campuses
                            local_or_non_local: $local_or_non_local
                            gender: $gender
                            filter_by_registration: $filter_by_registration
                            specialization: $specialization
                        ) {
                            error
                            error_code
                            message
                            data {
                            student_id
                            user_id
                            eid
                            name_en
                            name_ar
                            gender
                            birthdate
                            entity
                            campus_code
                            program_name
                            stream
                            study_program
                            program_code
                            cluster_e
                            primary_email
                            secondary_email
                            alumni_phone
                            primary_phone
                            secondary_phone
                            local_or_non_local
                            academic_year
                            ay
                            grad_year
                            academic_year_graduation
                            is_employed
                            university_country
                            university_name
                            college
                            major
                            current_status
                            current_status_reason
                            employmentInformations {
                                employer
                                position
                                aprehis_empl_position
                            }
                            }
                        }
                        }
                    `,
                    fetchPolicy: "network-only",
                    errorPolicy: 'all', // Continue on GraphQL errors
                    variables: {
                        page: pageNo,
                        limit: limitSize,
                        search: this.searchText,
                        entity: this.filterByEntity,
                        specialization: this.filterBySpecialization,
                        student_status: this.filterByStatus,
                        is_final_year_student: this.isFinalYearStudent,
                        is_awarded: this.isAwarded,
                        campuses: this.filterByCampuses,
                        local_or_non_local: this.filterByLocalOrNonLocal,
                        gender: this.filterByGender,
                        filter_by_registration: this.filterByRegistration,
                    },
                });

                const responseData = data.generateStudentsReport;

                // Check for errors in the response
                if (responseData.error) {
                    throw new Error(responseData.message || 'Server error occurred');
                }

                return responseData;
            } catch (error) {
                console.error('Error fetching data for export:', error);
                throw error; // Re-throw to handle in calling method
            }
        },
        exportToExcel(reportName) {
            const XLSX = xlsx;

            let header = [
                "STUDENT ID",
                "EID",
                "NAME IN ENGLISH",
                "NAME IN ARABIC",
                "GENDER",
                "DATE OF BIRTH",
                "ENTITY",
                "CAMPUS",
                "PROGRAM NAME",
            ];

            if (this.isATS) {
                header.push("STREAM");
            }

            let additionalFields = [
                "ALUMNI PHONE",
                "PRIMARY PHONE",
                "SECONDARY PHONE",
                "PRIMARY EMAIL",
                "SECONDARY EMAIL",
                "LOCAL OR NON LOCAL",
                "ACADEMIC YEAR",
                "GRADUATED YEAR",
                "UNIVERSITY COUNTRY",
                "UNIVERSITY NAME",
                "COLLEGE",
                "MAJOR",
                "CURRENT STATUS",
                "CURRENT STATUS REASON",
                "IS EMPLOYED",
                "EMPLOYER",
                "DESIGNATION",
                "IS REGISTERED",
            ];

            header = header.concat(additionalFields);

            const worksheet = XLSX.utils.json_to_sheet(this.generatedReport);

            worksheet["A1"] = { v: header[0], t: "s" };
            worksheet["B1"] = { v: header[1], t: "s" };
            worksheet["C1"] = { v: header[2], t: "s" };
            worksheet["D1"] = { v: header[3], t: "s" };
            worksheet["E1"] = { v: header[4], t: "s" };
            worksheet["F1"] = { v: header[5], t: "s" };
            worksheet["G1"] = { v: header[6], t: "s" };
            worksheet["H1"] = { v: header[7], t: "s" };
            worksheet["I1"] = { v: header[8], t: "s" };
            worksheet["J1"] = { v: header[9], t: "s" };
            worksheet["K1"] = { v: header[10], t: "s" };
            worksheet["L1"] = { v: header[11], t: "s" };
            worksheet["M1"] = { v: header[12], t: "s" };
            worksheet["N1"] = { v: header[13], t: "s" };
            worksheet["O1"] = { v: header[14], t: "s" };
            worksheet["P1"] = { v: header[15], t: "s" };
            worksheet["Q1"] = { v: header[16], t: "s" };
            worksheet["R1"] = { v: header[17], t: "s" };
            worksheet["S1"] = { v: header[18], t: "s" };
            worksheet["T1"] = { v: header[19], t: "s" };
            worksheet["U1"] = { v: header[20], t: "s" };
            worksheet["V1"] = { v: header[21], t: "s" };
            worksheet["W1"] = { v: header[22], t: "s" };
            worksheet["X1"] = { v: header[23], t: "s" };
            worksheet["Y1"] = { v: header[24], t: "s" };
            worksheet["Z1"] = { v: header[25], t: "s" };
            worksheet["Z1"] = { v: header[25], t: "s" };
            worksheet["AA1"] = { v: header[26], t: "s" };
            if (this.isATS) {
                worksheet["AB1"] = { v: header[27], t: "s" };
            }

            worksheet["!cols"] = [
                { width: 40 },
                { width: 40 },
                { width: 30 },
                { width: 30 },
                { width: 20 },
                { width: 30 },
                { width: 20 },
                { width: 20 },
                { width: 40 },
            ];

            // Add width for 'STREAM' if isATS is true
            if (this.isATS) {
                worksheet["!cols"].push({ width: 30 });
            }

            worksheet["!cols"] = worksheet["!cols"].concat([
                { width: 40 },
                { width: 20 },
                { width: 20 },
                { width: 20 },
                { width: 20 },
                { width: 20 },
                { width: 20 },
                { width: 20 },
                { width: 20 },
                { width: 40 },
                { width: 30 },
                { width: 30 },
                { width: 30 },
                { width: 40 },
                { width: 20 },
                { width: 40 },
                { width: 30 },
                { width: 20 },
            ]);

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
            XLSX.writeFile(workbook, `${reportName}.xlsx`);
            this.generatedReport = [];
            this.isExportExcel = false;
            this.isApiCallRecurisive = false;
        },
        closeFullModal() {
            this.showFullModal = false;
            this.showStudentProfileFullModal = false;
            this.studentId = null;
        },
        viewStudentProfile(studentId) {
            this.studentId = studentId;
            this.showStudentProfileFullModal = true;
        },
        isStudentAwarded(isAwarded) {
            if (isAwarded) {
                return true;
            } else {
                return false;
            }
        },
        setCheckboxVal(checkbox, studentId) {
            this.selectedStudentId = studentId;
            if (checkbox) {
                this.showUploadDocumentModal = true;
            }
        },
        closeBaseModal() {
            this.showUploadDocumentModal = false;
            this.selectedStudentId = null;
        },
        async onDocumentSubmit(documents) {
            if (documents.length > 0) {
                const formData = {
                    input_attachments: documents,
                };

                const CREATE_MUTATION = gql`
                    mutation AwardThisStudent(
                        $student_id: String!
                        $input: AwardThisStudentInput!
                    ) {
                        awardThisStudent(student_id: $student_id, input: $input) {
                            error
                            message
                        }
                    }
                `;

                this.isLoading = true;
                let error_msg = null;
                let success_msg = null;

                try {
                    await this.$apollo
                        .mutate({
                            mutation: CREATE_MUTATION,
                            variables: {
                                student_id: this.selectedStudentId,
                                input: formData,
                            },
                            context: {
                                headers: {
                                    "content-type": "multipart/form-data",
                                },
                            },
                        })
                        .then((data) => {
                            if (data.data.awardThisStudent.error) {
                                error_msg = data.data.awardThisStudent.message;
                            } else {
                                success_msg = data.data.awardThisStudent.message;
                            }
                        })
                        .catch((error) => {
                            error_msg = error;
                        });
                } catch (error) {
                    error_msg = error.message || "Failed to authenticate, try later.";
                }

                if (error_msg === null) {
                    this.closeBaseModal();
                    this.showToast(success_msg);
                } else {
                    this.$swal
                        .fire({
                            icon: "error",
                            title: "Oops...",
                            text: error_msg,
                        })
                        .then((result) => {
                            if (result.value || result.dismiss) {
                                this.closeBaseModal();
                            }
                        });
                }

                this.isLoading = false;
            }
        },
        showToast(message = "") {
            this.$apollo.queries.students.refetch();
            const Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 1000,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener("mouseenter", this.$swal.stopTimer);
                    toast.addEventListener("mouseleave", this.$swal.resumeTimer);
                },
            });
            Toast.fire({
                icon: "success",
                title: message,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
